import { useState, useRef } from "react";
import { toast } from "sonner";
import { ArrowLeftRight } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import SignaturePad from "@/components/SignaturePad";
import InsurancePicker from "@/components/InsurancePicker";

const EMPTY = { first_name: "", last_name: "", date_of_birth: "", phone: "", email: "", current_pharmacy: "" };

export default function Transfer() {
  const [form, setForm] = useState(EMPTY);
  const [approved, setApproved] = useState(false);
  const [insurance, setInsurance] = useState(null);
  const [loading, setLoading] = useState(false);
  const sigRef = useRef(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    if (!approved) return toast.error("Please approve the transfer of your profile.");
    if (sigRef.current?.isEmpty()) return toast.error("Please provide your digital signature.");
    setLoading(true);
    try {
      await api.post("/transfer/requests", { ...form, approved, signature: sigRef.current.toDataURL(), insurance });
      toast.success("Transfer request submitted! We'll handle the rest.");
      setForm(EMPTY); setApproved(false); sigRef.current.clear();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to submit.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl px-6 py-16">
      <div className="mb-8 flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-secondary text-primary"><ArrowLeftRight className="h-6 w-6" /></div>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Transfer a Prescription</h1>
          <p className="text-sm text-muted-foreground">We'll contact your current pharmacy and move your prescriptions to us.</p>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-5 rounded-2xl border border-border bg-card p-6 sm:p-8">
        <div className="grid gap-5 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="first_name">First name</Label>
            <Input id="first_name" data-testid="transfer-first-name" value={form.first_name} onChange={set("first_name")} required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="last_name">Last name</Label>
            <Input id="last_name" data-testid="transfer-last-name" value={form.last_name} onChange={set("last_name")} required />
          </div>
        </div>
        <div className="grid gap-5 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="date_of_birth">Date of birth</Label>
            <Input id="date_of_birth" type="date" data-testid="transfer-dob" value={form.date_of_birth} onChange={set("date_of_birth")} required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone">Phone number</Label>
            <Input id="phone" data-testid="transfer-phone" value={form.phone} onChange={set("phone")} required />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" data-testid="transfer-email" value={form.email} onChange={set("email")} required />
        </div>
        <div className="space-y-2">
          <Label htmlFor="pharmacy">Current pharmacy</Label>
          <Input id="pharmacy" data-testid="transfer-pharmacy" placeholder="Name & location of your current pharmacy" value={form.current_pharmacy} onChange={set("current_pharmacy")} required />
        </div>

        <div className="flex items-start gap-3 rounded-lg border border-border bg-secondary/50 p-4">
          <Checkbox id="approve" checked={approved} onCheckedChange={(v) => setApproved(!!v)} data-testid="transfer-approve" className="mt-0.5" />
          <Label htmlFor="approve" className="text-sm font-normal leading-relaxed">
            I approve of the transfer of my prescription profile from my current pharmacy to Crystal Pharmacy, and authorize Crystal Pharmacy to obtain my prescription records.
          </Label>
        </div>

        {approved && (
          <div className="rounded-lg border border-primary/30 bg-white p-4" data-testid="transfer-signature-section">
            <SignaturePad ref={sigRef} label="Digital signature" testId="transfer-signature" />
          </div>
        )}

        <InsurancePicker onChange={setInsurance} testPrefix="transfer" />

        <Button type="submit" disabled={loading || !approved} data-testid="transfer-submit" className="w-full rounded-full sm:w-auto sm:px-10">
          {loading ? "Submitting..." : "Submit Transfer"}
        </Button>
      </form>
    </div>
  );
}
